import type { KokoAnimationStateName } from "./animations";

export interface BirdVideoAsset {
  webm: string;
  poster: string;
}

export interface BirdVideoAltAsset extends BirdVideoAsset {
  id: string;
}

export interface BirdVideoStateAsset extends BirdVideoAsset {
  alts?: BirdVideoAltAsset[];
}

export interface BirdVariant {
  id: string;
  label: string;
  scale?: number;
  states: Partial<Record<KokoAnimationStateName, BirdVideoStateAsset>>;
}

export interface BirdManifest {
  version: 2;
  variants: BirdVariant[];
}

export interface BirdStyleOption {
  id: string;
  label: string;
  scale: number;
  kind: "pixel" | "video";
  variant?: BirdVariant;
}

const STATE_NAMES: readonly KokoAnimationStateName[] = [
  "flight",
  "perched",
  "listening",
  "thinking",
  "talking",
  "pointing",
  "swoop",
];

const warnedBirdScaleVariants = new Set<string>();

export async function fetchBirdManifest(baseUrl: string): Promise<BirdManifest> {
  const response = await fetch(`${baseUrl.replace(/\/$/, "")}/manifest.json`);
  if (!response.ok) {
    throw new Error(`manifest request failed with ${response.status}`);
  }
  const value: unknown = await response.json();
  if (!isBirdManifest(value)) {
    throw new Error("invalid bird manifest");
  }
  return value;
}

export function findBirdVariant(
  manifest: BirdManifest,
  variantId: string,
): BirdVariant | null {
  return manifest.variants.find((variant) => variant.id === variantId) ?? null;
}

export function buildBirdStyleOptions(
  manifest: BirdManifest | null,
): readonly BirdStyleOption[] {
  const options: BirdStyleOption[] = [
    {
      id: "pixel",
      label: "Pixel",
      scale: 1,
      kind: "pixel",
    },
  ];

  if (!manifest) return options;
  for (const variant of manifest.variants) {
    options.push({
      id: variant.id,
      label: variant.label,
      scale: getBirdVariantScale(variant),
      kind: "video",
      variant,
    });
  }
  return options;
}

export function resolveBirdStateAsset(
  variant: BirdVariant,
  stateName: KokoAnimationStateName,
  altId: string | null | undefined = null,
): { asset: BirdVideoAsset; altId: string | null } | null {
  const stateAsset = variant.states[stateName];
  if (!stateAsset) return null;
  if (altId === null || altId === undefined) {
    return { asset: stateAsset, altId: null };
  }
  const altAsset = stateAsset.alts?.find((alt) => alt.id === altId) ?? null;
  return altAsset
    ? { asset: altAsset, altId: altAsset.id }
    : { asset: stateAsset, altId: null };
}

export function getBirdStateAltIds(
  variant: BirdVariant,
  stateName: KokoAnimationStateName,
): readonly string[] {
  return variant.states[stateName]?.alts?.map((alt) => alt.id) ?? [];
}

export function getBirdVariantScale(variant: BirdVariant): number {
  const value = variant.scale;
  // Wide range: the clips are framed with generous transparent margin so
  // the full wingspan never clips, which means the bird occupies a small
  // fraction of its 512 box and needs a large display scale (~3-4x) to
  // read at the intended on-screen size.
  if (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0.5 &&
    value <= 6
  ) {
    return value;
  }

  if (!warnedBirdScaleVariants.has(variant.id)) {
    warnedBirdScaleVariants.add(variant.id);
    if (value !== undefined) {
      console.warn(
        `[koko] bird variant "${variant.id}" has invalid scale ${String(value)}; using 1.0.`,
      );
    }
  }
  return 1;
}

function isBirdManifest(value: unknown): value is BirdManifest {
  if (!isRecord(value) || value.version !== 2 || !Array.isArray(value.variants)) {
    return false;
  }
  return value.variants.every(isBirdVariant);
}

function isBirdVariant(value: unknown): value is BirdVariant {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    value.id.length === 0 ||
    typeof value.label !== "string" ||
    !isRecord(value.states)
  ) {
    return false;
  }

  return Object.entries(value.states).every(([stateName, asset]) => {
    if (!STATE_NAMES.includes(stateName as KokoAnimationStateName)) return false;
    return isBirdStateAsset(asset);
  });
}

function isBirdStateAsset(value: unknown): value is BirdVideoStateAsset {
  if (
    !isRecord(value) ||
    typeof value.webm !== "string" ||
    value.webm.length === 0 ||
    typeof value.poster !== "string" ||
    value.poster.length === 0
  ) {
    return false;
  }

  if (value.alts === undefined) return true;
  if (!Array.isArray(value.alts)) return false;
  const altIds = new Set<string>();
  return value.alts.every((alt) => {
    if (!isBirdAltAsset(alt)) return false;
    if (altIds.has(alt.id)) return false;
    altIds.add(alt.id);
    return true;
  });
}

function isBirdAltAsset(value: unknown): value is BirdVideoAltAsset {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    value.id.length > 0 &&
    typeof value.webm === "string" &&
    value.webm.length > 0 &&
    typeof value.poster === "string" &&
    value.poster.length > 0
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

